import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import GatewayDocuments from './GatewayDocuments';
import PaymentGatewayRateTable from './PaymentGatewayRateTable';
import { CopyIcon } from './Icons';
import { getCredentialDefaultValue, getCredentialKey, normalizeCredentialFields } from '../utils/credentialFields';
import { isEnvComplete, resolveProviderStatus } from '../utils/providerConfiguration';

const MerchantProviderConfigurationModal = ({ isOpen, onClose, editingProvider, setEditingProvider, modalMasterProvider,
  canEdit, canViewProviderFinancial, canEditProviderFinancial, onApply, error }) => {
  const [modalEnvSections, setModalEnvSections] = useState({ staging: false, production: false });
  useEffect(() => { setModalEnvSections({ staging: false, production: false }); }, [isOpen]);
  const handleCopyValue = (text, label) => {
    if (text) navigator.clipboard.writeText(text).then(() => alert(`${label} copied to clipboard!`)).catch(() => {});
  };
  return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Configure ${editingProvider?.name}`}
        maxWidth={editingProvider?.paymentGateway ? '1100px' : undefined}
      >
        {editingProvider && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {error && <p role="alert" style={{ color: 'var(--error)', margin: 0 }}>{error}</p>}
            {(() => {
              const costingRate = modalMasterProvider?.costing_rates ?? 0;
              const effectiveCostingRate = editingProvider.is_client_line ? 0 : costingRate;
              const sellingRate = editingProvider?.selling_rate ?? 0;
              const margin = sellingRate - effectiveCostingRate;
              const credentialKeys = normalizeCredentialFields(Array.isArray(modalMasterProvider?.api_credential_keys)
                ? modalMasterProvider.api_credential_keys
                : editingProvider?.api_credential_keys);
              const boCredentialKeys = normalizeCredentialFields(Array.isArray(modalMasterProvider?.bo_credential_keys)
                ? modalMasterProvider.bo_credential_keys
                : editingProvider?.bo_credential_keys);
              const callbackDefs = Array.isArray(modalMasterProvider?.callback_urls)
                ? modalMasterProvider.callback_urls
                : (Array.isArray(editingProvider?.callback_urls) ? editingProvider.callback_urls : []);
              const credentialStatus = {
                production: isEnvComplete(editingProvider.credentials?.production, [...credentialKeys, ...boCredentialKeys], callbackDefs),
                staging: isEnvComplete(editingProvider.credentials?.staging, [...credentialKeys, ...boCredentialKeys], callbackDefs)
              };
              const categoryName = editingProvider.paymentGateway ? (modalMasterProvider?.category || []).map(item => item.name).join(', ') : modalMasterProvider?.provider_id?.category?.name || editingProvider?.category || '-';
              const subCategoryName = modalMasterProvider?.provider_id?.sub_category?.name || editingProvider?.sub_category || '-';
              const sourceName = modalMasterProvider?.source?.name || editingProvider?.source || '-';
              const providerType = editingProvider.paymentGateway ? 'Payment Gateway' : modalMasterProvider?.type || editingProvider?.type || '-';

              return (
                <div style={{ backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)', padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>{editingProvider.name}</div>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.35rem', flexWrap: 'wrap' }}>
                        <span style={{ backgroundColor: 'var(--bg-card)', padding: '0.1rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '0.7rem', fontWeight: 600 }}>{providerType}</span>
                        <span style={{ color: 'var(--text-muted)' }}>•</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{sourceName}</span>
                        <span style={{ color: 'var(--text-muted)' }}>•</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 600 }}>{categoryName}{subCategoryName !== '-' ? ` / ${subCategoryName}` : ''}</span>
                      </div>
                    </div>
                    {canViewProviderFinancial && !editingProvider.paymentGateway && (
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Costing</div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 700 }}>{effectiveCostingRate}%</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Selling</div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 700 }}>{sellingRate}%</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Margin</div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 700, color: margin > 0 ? 'var(--success)' : (margin < 0 ? 'var(--error)' : 'var(--text-secondary)') }}>
                          {margin > 0 ? '+' : ''}{margin.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      borderRadius: '9999px',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      backgroundColor: credentialStatus.production ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      color: credentialStatus.production ? 'var(--success)' : 'var(--error)'
                    }}>PROD {credentialStatus.production ? 'READY' : 'MISSING'}</span>
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      borderRadius: '9999px',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      backgroundColor: credentialStatus.staging ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      color: credentialStatus.staging ? 'var(--success)' : 'var(--error)'
                    }}>STG {credentialStatus.staging ? 'READY' : 'MISSING'}</span>
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      backgroundColor: resolveProviderStatus(editingProvider) === 'active'
                        ? 'rgba(16, 185, 129, 0.1)'
                        : (resolveProviderStatus(editingProvider) === 'draft' ? 'rgba(251, 191, 36, 0.18)' : 'rgba(239, 68, 68, 0.1)'),
                      color: resolveProviderStatus(editingProvider) === 'active'
                        ? 'var(--success)'
                        : (resolveProviderStatus(editingProvider) === 'draft' ? 'var(--warning)' : 'var(--error)')
                    }}>{resolveProviderStatus(editingProvider).toUpperCase()}</span>
                  </div>
                </div>
              );
            })()}
            {editingProvider.paymentGateway && canViewProviderFinancial && <PaymentGatewayRateTable provider={modalMasterProvider}
              rules={editingProvider.selling_rate_rules} defaultRate={editingProvider.selling_rate ?? 0} showCosting clientLine={editingProvider.is_client_line}
              disabled={!canEdit || !canEditProviderFinancial}
              onChange={selling_rate_rules => setEditingProvider({ ...editingProvider, selling_rate_rules })} />}
            {editingProvider.paymentGateway && <>
              <label style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>Description
                <textarea className="input" rows={3} value={editingProvider.description || ''} disabled={!canEdit}
                  onChange={event => setEditingProvider({ ...editingProvider, description: event.target.value })} style={{ fontFamily: 'inherit', resize: 'vertical' }} />
              </label>
              <GatewayDocuments documents={editingProvider.documents || []} canEdit={canEdit}
                onChange={documents => setEditingProvider({ ...editingProvider, documents })} />
            </>}
            <div style={{ display: 'grid', gridTemplateColumns: canViewProviderFinancial && !editingProvider.paymentGateway ? '1fr 1fr' : '1fr', gap: '1.5rem' }}>
              {canViewProviderFinancial && !editingProvider.paymentGateway && (
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Selling Rate (%)</label>
                <input
                  type="number" className="input" value={editingProvider.selling_rate}
                  onChange={(e) => setEditingProvider({ ...editingProvider, selling_rate: parseFloat(e.target.value) })}
                  min="0" max="100" step="0.01" disabled={!canEdit || !canEditProviderFinancial}
                />
              </div>
              )}
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Status</label>
                <select
                  className="input"
                  value={resolveProviderStatus(editingProvider)}
                  onChange={(e) => setEditingProvider({
                    ...editingProvider,
                    status: e.target.value,
                    is_active: e.target.value === 'active'
                  })}
                  disabled={!canEdit}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>
            {canViewProviderFinancial && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)' }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Is Client Line</span>
              <label className="switch">
                <input
                  type="checkbox"
                  aria-label="Is Client Line"
                  checked={!!editingProvider.is_client_line}
                  onChange={(e) => setEditingProvider({ ...editingProvider, is_client_line: e.target.checked })}
                  disabled={!canEdit || !canEditProviderFinancial}
                />
                <span className="slider"></span>
              </label>
            </div>
            )}

            <div style={{ backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)', padding: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-primary)' }}>Group Links</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>{editingProvider.paymentGateway ? 'Defaults from L1 Payment Gateway Provider; changes here apply only to this merchant gateway.' : 'Defaults from L1 Master Provider; changes here apply only to this merchant provider.'}</div>
                </div>
                {canEdit && (editingProvider.master_provider_group_links || []).length > 0 && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                    onClick={() => setEditingProvider({ ...editingProvider, group_links: editingProvider.master_provider_group_links })}
                  >
                    Reset to L1
                  </button>
                )}
              </div>
              {((editingProvider.group_links?.length ?? 0) > 0 ? editingProvider.group_links : [{ name: '', url: '' }]).map((link, idx) => {
                const linkName = typeof link === 'string' ? `Link ${idx + 1}` : (link?.name || '');
                const linkUrl = typeof link === 'string' ? link : (link?.url || '');
                return (
                <div key={`provider-group-link-${idx}`} style={{ display: 'grid', gridTemplateColumns: canEdit && (editingProvider.group_links?.length || 0) > 1 ? 'minmax(140px, 0.35fr) minmax(220px, 1fr) auto' : 'minmax(140px, 0.35fr) minmax(220px, 1fr)', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    className="input"
                    placeholder="Name"
                    value={linkName}
                    onChange={(e) => {
                      const nextLinks = [...(editingProvider.group_links || [])];
                      const current = nextLinks[idx];
                      nextLinks[idx] = typeof current === 'string'
                        ? { name: e.target.value, url: current }
                        : { ...(current || {}), name: e.target.value };
                      setEditingProvider({ ...editingProvider, group_links: nextLinks });
                    }}
                    disabled={!canEdit}
                  />
                  <input
                    type="url"
                    className="input"
                    placeholder="URL"
                    value={linkUrl}
                    onChange={(e) => {
                      const nextLinks = [...(editingProvider.group_links || [])];
                      const current = nextLinks[idx];
                      nextLinks[idx] = typeof current === 'string'
                        ? { name: `Link ${idx + 1}`, url: e.target.value }
                        : { ...(current || {}), url: e.target.value };
                      setEditingProvider({ ...editingProvider, group_links: nextLinks });
                    }}
                    disabled={!canEdit}
                  />
                  {canEdit && (editingProvider.group_links?.length || 0) > 1 && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ padding: '0 0.75rem' }}
                      onClick={() => {
                        const nextLinks = (editingProvider.group_links || []).filter((_, i) => i !== idx);
                        setEditingProvider({ ...editingProvider, group_links: nextLinks.length ? nextLinks : [{ name: '', url: '' }] });
                      }}
                    >×</button>
                  )}
                </div>
              );
              })}
              {canEdit && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}
                  onClick={() => setEditingProvider({ ...editingProvider, group_links: [...(editingProvider.group_links || []), { name: '', url: '' }] })}
                >
                  + Add Group Link
                </button>
              )}
            </div>

            <div style={{ backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)', overflow: 'hidden' }}>
              {['production', 'staging'].map(env => (
                (() => {
                  const credentialKeys = normalizeCredentialFields(Array.isArray(modalMasterProvider?.api_credential_keys)
                    ? modalMasterProvider.api_credential_keys
                    : editingProvider?.api_credential_keys);
                  const boCredentialKeys = normalizeCredentialFields(Array.isArray(modalMasterProvider?.bo_credential_keys)
                    ? modalMasterProvider.bo_credential_keys
                    : editingProvider?.bo_credential_keys);
                  const callbackDefs = Array.isArray(modalMasterProvider?.callback_urls)
                    ? modalMasterProvider.callback_urls
                    : (Array.isArray(editingProvider?.callback_urls) ? editingProvider.callback_urls : []);
                  const isReady = isEnvComplete(editingProvider.credentials?.[env], [...credentialKeys, ...boCredentialKeys], callbackDefs);
                  return (
                <div key={env} style={{ borderBottom: env === 'staging' ? 'none' : '1px solid var(--border-light)' }}>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setModalEnvSections(prev => ({ ...prev, [env]: !prev[env] }))}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.9rem 1.25rem',
                      borderRadius: 0,
                      backgroundColor: 'var(--bg-card)',
                      border: 'none'
                    }}
                  >
                    <span style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                      {env === 'production' ? 'Production (Live)' : 'Staging (Test)'}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{
                        padding: '0.2rem 0.5rem',
                        borderRadius: '9999px',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        backgroundColor: isReady ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: isReady ? 'var(--success)' : 'var(--error)'
                      }}>{isReady ? 'READY' : 'MISSING'}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {modalEnvSections[env] ? 'Hide' : 'Show'}
                      </span>
                    </span>
                  </button>
                  {modalEnvSections[env] && (
                    <div style={{ padding: '1.25rem', borderTop: '1px solid var(--border-light)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>Backoffice URLs</label>
                          {((editingProvider.credentials?.[env]?.bo_urls?.length ?? 0) > 0
                            ? editingProvider.credentials?.[env]?.bo_urls
                            : ['']
                          ).map((url, idx) => (
                            <div key={`${env}-bo-url-${idx}`} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <input
                                type="text" className="input" placeholder={`Enter ${env} BO URL`}
                                value={url || ''}
                                onChange={(e) => {
                                  const nextUrls = [...(editingProvider.credentials?.[env]?.bo_urls || [])];
                                  nextUrls[idx] = e.target.value;
                                  setEditingProvider({
                                    ...editingProvider,
                                    credentials: {
                                      ...editingProvider.credentials,
                                      [env]: { ...editingProvider.credentials?.[env], bo_urls: nextUrls }
                                    }
                                  });
                                }}
                                disabled={!canEdit}
                              />
                              {canEdit && (editingProvider.credentials?.[env]?.bo_urls?.length || 0) > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  style={{ padding: '0 0.75rem' }}
                                  onClick={() => {
                                    const nextUrls = (editingProvider.credentials?.[env]?.bo_urls || []).filter((_, i) => i !== idx);
                                    setEditingProvider({
                                      ...editingProvider,
                                      credentials: {
                                        ...editingProvider.credentials,
                                        [env]: { ...editingProvider.credentials?.[env], bo_urls: nextUrls.length ? nextUrls : [''] }
                                      }
                                    });
                                  }}
                                >×</button>
                              )}
                            </div>
                          ))}
                          {canEdit && (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}
                              onClick={() => {
                                const nextUrls = [...(editingProvider.credentials?.[env]?.bo_urls || []), ''];
                                setEditingProvider({
                                  ...editingProvider,
                                  credentials: {
                                    ...editingProvider.credentials,
                                    [env]: { ...editingProvider.credentials?.[env], bo_urls: nextUrls }
                                  }
                                });
                              }}
                            >+ Add Backoffice URL</button>
                          )}
                        </div>
                        {boCredentialKeys.length > 0 && (
                          <div style={{ marginTop: '0.75rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>BO Credentials</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                              {boCredentialKeys.map(field => {
                                const key = getCredentialKey(field);
                                const defaultValue = getCredentialDefaultValue(field);
                                return (
                                <div key={`${env}-bo-${key}`}>
                                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                    {key}
                                  </label>
                                  <input
                                    type="text"
                                    className="input"
                                    placeholder={defaultValue ? `Default: ${defaultValue}` : `Enter ${env} ${key}`}
                                    value={editingProvider.credentials?.[env]?.[key] || ''}
                                    onChange={(e) => setEditingProvider({
                                      ...editingProvider,
                                      credentials: {
                                        ...editingProvider.credentials,
                                        [env]: { ...editingProvider.credentials?.[env], [key]: e.target.value }
                                      }
                                    })}
                                    disabled={!canEdit}
                                  />
                                  {defaultValue && (
                                    <div style={{ marginTop: '0.2rem', fontSize: '0.68rem', color: 'var(--text-muted)' }}>Default: {defaultValue}</div>
                                  )}
                                </div>
                              );
                              })}
                            </div>
                          </div>
                        )}
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>API URLs</label>
                          {((editingProvider.credentials?.[env]?.api_urls?.length ?? 0) > 0
                            ? editingProvider.credentials?.[env]?.api_urls
                            : ['']
                          ).map((url, idx) => (
                            <div key={`${env}-api-url-${idx}`} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <input
                                type="text" className="input" placeholder={`Enter ${env} API URL`}
                                value={url || ''}
                                onChange={(e) => {
                                  const nextUrls = [...(editingProvider.credentials?.[env]?.api_urls || [])];
                                  nextUrls[idx] = e.target.value;
                                  setEditingProvider({
                                    ...editingProvider,
                                    credentials: {
                                      ...editingProvider.credentials,
                                      [env]: { ...editingProvider.credentials?.[env], api_urls: nextUrls }
                                    }
                                  });
                                }}
                                disabled={!canEdit}
                              />
                              {canEdit && (editingProvider.credentials?.[env]?.api_urls?.length || 0) > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  style={{ padding: '0 0.75rem' }}
                                  onClick={() => {
                                    const nextUrls = (editingProvider.credentials?.[env]?.api_urls || []).filter((_, i) => i !== idx);
                                    setEditingProvider({
                                      ...editingProvider,
                                      credentials: {
                                        ...editingProvider.credentials,
                                        [env]: { ...editingProvider.credentials?.[env], api_urls: nextUrls.length ? nextUrls : [''] }
                                      }
                                    });
                                  }}
                                >×</button>
                              )}
                            </div>
                          ))}
                          {canEdit && (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}
                              onClick={() => {
                                const nextUrls = [...(editingProvider.credentials?.[env]?.api_urls || []), ''];
                                setEditingProvider({
                                  ...editingProvider,
                                  credentials: {
                                    ...editingProvider.credentials,
                                    [env]: { ...editingProvider.credentials?.[env], api_urls: nextUrls }
                                  }
                                });
                              }}
                            >+ Add API URL</button>
                          )}
                        </div>
                        {credentialKeys.length > 0 && (
                          <div style={{ marginTop: '0.75rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>API Credentials</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                              {credentialKeys.map(field => {
                                const key = getCredentialKey(field);
                                const defaultValue = getCredentialDefaultValue(field);
                                return (
                                <div key={`${env}-${key}`}>
                                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                    {key}
                                  </label>
                                  <input
                                    type="text"
                                    className="input"
                                    placeholder={defaultValue ? `Default: ${defaultValue}` : `Enter ${env} ${key}`}
                                    value={editingProvider.credentials?.[env]?.[key] || ''}
                                    onChange={(e) => setEditingProvider({
                                      ...editingProvider,
                                      credentials: {
                                        ...editingProvider.credentials,
                                        [env]: { ...editingProvider.credentials?.[env], [key]: e.target.value }
                                      }
                                    })}
                                    disabled={!canEdit}
                                  />
                                  {defaultValue && (
                                    <div style={{ marginTop: '0.2rem', fontSize: '0.68rem', color: 'var(--text-muted)' }}>Default: {defaultValue}</div>
                                  )}
                                </div>
                              );
                              })}
                            </div>
                          </div>
                        )}
                        {(Array.isArray(modalMasterProvider?.callback_urls) ? modalMasterProvider.callback_urls.length > 0 : Array.isArray(editingProvider?.callback_urls) && editingProvider.callback_urls.length > 0) && (
                          <div style={{ marginTop: '0.75rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>{editingProvider.paymentGateway ? 'Callback Path / URL' : 'Callback URLs (Path Only)'}</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                              {(Array.isArray(modalMasterProvider?.callback_urls) ? modalMasterProvider.callback_urls : (editingProvider?.callback_urls || [])).map(cb => (
                                <div key={`${env}-cb-${cb.name}`}>
                                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                    {cb.name}
                                  </label>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: '0.5rem',
                                      padding: '0.625rem 0.875rem',
                                      fontSize: '0.875rem',
                                      color: 'var(--text-secondary)',
                                      backgroundColor: 'var(--bg-soft)',
                                      border: '1px dashed var(--border-light)',
                                      borderRadius: 'var(--radius-md)'
                                    }}
                                  >
                                    {editingProvider.paymentGateway ? <input className="input" type="text" aria-label={`${env} ${cb.name} callback`}
                                      placeholder="Enter callback path or URL" value={editingProvider.credentials?.[env]?.callback_paths?.[cb.name] || ''}
                                      disabled={!canEdit} onChange={event => setEditingProvider({ ...editingProvider, credentials: {
                                        ...editingProvider.credentials, [env]: { ...editingProvider.credentials?.[env], callback_paths: {
                                          ...editingProvider.credentials?.[env]?.callback_paths, [cb.name]: event.target.value
                                        } }
                                      } })} /> : <span>{editingProvider.credentials?.[env]?.callback_paths?.[cb.name] || cb.path || '-'}</span>}
                                    <button
                                      type="button"
                                      onClick={() => handleCopyValue(editingProvider.credentials?.[env]?.callback_paths?.[cb.name] || cb.path || '', `${cb.name} Callback`)}
                                      style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.2rem', display: 'flex', alignItems: 'center', borderRadius: '4px' }}
                                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(var(--primary-rgb), 0.1)'}
                                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                      title="Copy"
                                    >
                                      <CopyIcon size={12} />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                  );
                })()
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
              <button type="button" className="btn" onClick={onClose}>Close</button>
              {canEdit && (
                <button type="button" className="btn btn-primary" onClick={onApply}>Apply Settings</button>
              )}
            </div>
          </div>
        )}
      </Modal>
  );
};

export default MerchantProviderConfigurationModal;
